import React, { useMemo } from "react";
import { Box, Container } from "@mui/material";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";

import { buildColumnsSemaforo } from "./columns";
import SemaforoTopToolbar from "./SemaforoTopToolbar ";

const SemaforoTable = ({
  data = [],
  loading,
  refresh,
  showToast,
  onToolbarAction = () => {},
  onRowClick = () => {},
  envasado = 0,
  kpis,
  totales,
}) => {
  const columns = useMemo(() => buildColumnsSemaforo(), []);

  const getSemaforoStyles = (semaforo) => {
    const config = {
      ROJO: {
        borderLeft: "4px solid #ef4444",
        hoverBg: "#fef2f2",
        dotColor: "#ef4444",
        dotGlow: "#fee2e2",
      },
      AMARILLO: {
        borderLeft: "4px solid #f59e0b",
        hoverBg: "#fffbeb",
        dotColor: "#f59e0b",
        dotGlow: "#fef3c7",
      },
      BLANCO: {
        borderLeft: "4px solid #22c55e",
        hoverBg: "#f0fdf4",
        dotColor: "#22c55e",
        dotGlow: "#dcfce7",
      },
    };
    return config[semaforo] || config.BLANCO;
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: {
      pageIndex: 0,
      pageSize: 100,
    },
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 14,
      color: "black",
      fontWeight: "normal",
      paddingTop: 1,
      paddingBottom: 1,
      backgroundColor: "rgba(212, 230, 241, 0.8)",
    },
  };

  return (
    <Container
      maxWidth="100%"
      disableGutters
      sx={{ py: 2, px: { xs: 1, sm: 2, md: 3 } }}
    >
      {/* Toolbar superior con KPIs */}
      <SemaforoTopToolbar
        onAction={onToolbarAction}
        envasado={envasado}
        kpis={kpis}
        onRefresh={refresh}
        loading={loading}
      />

      {/* Tabla con sombra y bordes redondeados */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
          bgcolor: "white",
        }}
      >
        <MaterialReactTable
          columns={columns}
          data={data}
          meta={{ totales }} //Prop para pasarle los totales a las columnas
          localization={MRT_Localization_ES}
          initialState={initialState}
          enableSorting
          enableColumnActions={false}
          enableColumnFilters={false}
          enableColumnOrdering={false}
          enableStickyFooter
          enableHiding={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableStickyHeader
          // 🔍 Búsqueda global estilizada
          muiSearchTextFieldProps={{
            size: "small",
            placeholder: "Buscar cliente...",
            sx: {
              minWidth: 320,
              "& .MuiInputBase-root": {
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                "&:hover": { borderColor: "#cbd5e1" },
                "&.Mui-focused": {
                  borderColor: "#0f172a",
                  boxShadow: "0 0 0 3px rgba(15,23,42,0.08)",
                },
              },
            },
          }}
          // 📋 Header de tabla
          muiTableHeadCellProps={{
            sx: {
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              py: 1.5,
              px: 2,
              bgcolor: "#f1f5f9",
              borderBottom: "1px solid #e2e8f0",
              "& .Mui-TableHeadCell-Content": {
                justifyContent: "flex-start",
              },
            },
          }}
          // 📄 Celdas del body
          muiTableBodyCellProps={{
            sx: {
              py: 1.2,
              px: 2,
              fontSize: 13,
              color: "#334155",
              borderBottom: "1px solid #f1f5f9",
              transition: "all 0.15s ease",
            },
          }}
          // 🖱️ Filas interactivas con semáforo
          muiTableBodyRowProps={({ row }) => {
            const semaforo = row.original.semaforo;
            const styles = getSemaforoStyles(semaforo);

            return {
              onClick: () => onRowClick(row.original),
              sx: {
                cursor: "pointer",
                borderLeft: styles.borderLeft,
                transition: "all 0.15s ease",
                position: "relative",
                "&:hover": {
                  backgroundColor: styles.hoverBg,
                  transform: "translateX(2px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  zIndex: 1,
                },
                "&:active": {
                  transform: "translateX(0)",
                },
              },
            };
          }}
          // 📦 Contenedor con altura máxima
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 240px)",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#cbd5e1",
                borderRadius: 3,
              },
            },
          }}
          // ⚙️ Estado de carga
          state={{
            isLoading: loading,
            showSkeletons: loading,
            showLoadingOverlay: loading,
          }}
          // 🎯 Toolbar personalizado (vacío porque ya tenemos el nuestro arriba)
          renderTopToolbarCustomActions={() => null}
          // Ajustes del toolbar de MRT
          muiTopToolbarProps={{
            sx: {
              display: "none", // Ocultamos el toolbar default de MRT
            },
          }}
        />
      </Box>

      {/* 💡 Hint inferior */}
      <Box sx={{ my: 1.5, p: 1, textAlign: "center" }}>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: "#94a3b8",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          💡 Hacé clic en cualquier fila para ver la cuenta corriente detallada
        </Box>
      </Box>
    </Container>
  );
};

export default SemaforoTable;
